import React, { useContext, useEffect, useRef, useState } from "react"
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button } from "reactstrap"
import { TipoAgendaContext } from "../../context/TipoAgendaContext"
import { UpdateTipoAgendaType } from "../../context/hooks/useTipoAgenda"
import ModalConfirm from "../ModalConfirm/ModalConfirm"

interface Props {
    isOpen: boolean
    toggle: () => void
    tipoAgenda: UpdateTipoAgendaType | null
    onUpdated?: () => void
    onDeleted?: () => void
}

export const EditTipoAgendaModal: React.FC<Props> = ({ isOpen, toggle, tipoAgenda, onUpdated, onDeleted }) => {
    const { updateTipoAgenda, deleteTipoAgenda } = useContext(TipoAgendaContext)

    const isMountedRef = useRef(true)
    const [submitting, setSubmitting] = useState(false)

    const [form, setForm] = useState<UpdateTipoAgendaType | null>(null)
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (isOpen && tipoAgenda) {
            setForm({ ...tipoAgenda })
            setSubmitting(false)
        }
        if (!isOpen) {
            setSubmitting(false)
            // limpa o form após fechar (evita renderizações “meio abertas”)
            setForm(null)
        }
    }, [isOpen, tipoAgenda])

    const closeThen = (cb?: () => void) => {
        toggle()
        if (cb) {
            requestAnimationFrame(() => {
                if (isMountedRef.current) cb()
            })
        }
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setForm((prev) => (prev ? { ...prev, [name]: value } : prev))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!form || submitting) return

        const desc = (form.description || "").trim()
        if (!desc) return

        setSubmitting(true)
        try {
            const ok = await updateTipoAgenda({ ...form, description: desc })
            if (ok) {
                closeThen(onUpdated)
            }
        } finally {
            if (isMountedRef.current) setSubmitting(false)
        }
    }

    const handleDelete = () => {
        if (!form || submitting) return
        setConfirmDeleteOpen(true)
    }

    const executeDelete = async () => {
        if (!form) return
        setConfirmDeleteOpen(false)
        setSubmitting(true)
        try {
            const ok = await deleteTipoAgenda(form.id)
            if (ok) {
                closeThen(onDeleted)
            }
        } finally {
            if (isMountedRef.current) setSubmitting(false)
        }
    }

    return (
        <Modal isOpen={isOpen} toggle={submitting ? undefined : toggle} backdrop="static" size="lg">
            <ModalHeader toggle={submitting ? undefined : toggle}>Editar informações do tipo de agenda</ModalHeader>
            <ModalBody>
                {!form ? null : (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label className="form-control-label">Tipo de agenda</Label>
                            <Input
                                className="form-control-alternative"
                                name="description"
                                value={form.description}
                                onChange={onChange}
                                required
                                disabled={submitting}
                                autoFocus
                            />
                        </FormGroup>

                        <div className="d-flex justify-content-between mt-3">
                            <Button
                                type="button"
                                color="danger"
                                onClick={handleDelete}
                                disabled={submitting}
                            >
                                {submitting ? "Processando..." : "Excluir"}
                            </Button>

                            <div>
                                <Button style={{ background: "#585858", color: "#fff" }} type="submit" disabled={submitting}>
                                    {submitting ? "Salvando..." : "Salvar alterações"}
                                </Button>
                                <Button
                                    type="button"
                                    style={{ background: "#585858", color: "#fff" }}
                                    className="ml-2"
                                    onClick={() => closeThen()}
                                    disabled={submitting}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </ModalBody>

            <ModalConfirm
                openModal={confirmDeleteOpen}
                closeModal={() => setConfirmDeleteOpen(false)}
                handleConfirm={executeDelete}
                title="Excluir tipo de agenda"
                message="Tem certeza que deseja excluir este tipo de agenda? Esta ação não pode ser desfeita."
                loading={submitting}
            />
        </Modal>
    )
}

export default EditTipoAgendaModal